import { useState, useCallback } from 'react'
import type React from 'react'
import cn from 'classnames'
import PermissionOptionList from 'components/permissions/PermissionOptionList'
import { format } from 'date-fns'
import KindSelectModal from './KindSelectModal'
import type { KindMap } from 'types/domain'

import './footerMeta.less'

type PermissionUser = {
  id: string
  username?: string
  index?: number
  [key: string]: unknown
}

type FooterObject = {
  id?: string
  modified: number
  data: {
    readers: Record<string, PermissionUser>
    writers: Record<string, PermissionUser>
    creator?: PermissionUser
    content?: string
    kind?: string
  }
}

type FooterMetaProps = {
  object: FooterObject
  kinds?: KindMap
  showMeta: boolean
  isOpen: boolean
  isEditing?: boolean
  permissionsVisible: boolean
  onSelectKind?: (kindId: string) => void
  onKindModalOpenChange?: (isOpen: boolean) => void
  objectType?: string
  editOneKind?: (payload: { id: string; label: string; styles: Record<string, unknown> }) => void
  createOneKind?: (payload: { id: string; label: string; styles: { color: string } }) => void
}

const FooterMeta = ({
  object,
  kinds,
  showMeta,
  isOpen,
  isEditing,
  onSelectKind: onSelectKindProp,
  onKindModalOpenChange,
  objectType,
  editOneKind,
  createOneKind,
}: FooterMetaProps) => {
  const [kindModalOpen, setKindModalOpen] = useState(false)

  const isActive = isOpen && isEditing
  const [prevIsActive, setPrevIsActive] = useState(isActive)

  if (prevIsActive !== isActive) {
    setPrevIsActive(isActive)
    if (!isActive && kindModalOpen) {
      setKindModalOpen(false)
    }
  }

  const openKindModal = useCallback((e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (!isActive) return
    onKindModalOpenChange?.(true)
    setKindModalOpen(true)
  }, [isActive, onKindModalOpenChange])

  const closeKindModal = useCallback(() => {
    setKindModalOpen(false)
    onKindModalOpenChange?.(false)
  }, [onKindModalOpenChange])

  const onKindIndicatorKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isActive) return
    const key = e.key
    if (key === 'Enter' || key === ' ') {
      openKindModal(e)
    }
  }, [isActive, openKindModal])

  const onSelectKind = useCallback((kindId: string) => {
    onSelectKindProp?.(kindId)
    closeKindModal()
  }, [onSelectKindProp, closeKindModal])

  if (!showMeta) {
    return null
  }

  const { modified } = object
  const { readers, writers, creator } = object.data
  const modifiedString = isOpen ? format(new Date(modified), "MMMM do yyyy, h:mm:ss a") : undefined

  const kindId = object.data?.kind || ''
  const kind = kinds?.[kindId]
  const kindLabel = kind?.data?.label
  const kindColor = kind?.data?.styles?.color
  const kindLabelToShow = kindLabel || 'Set kind'

  const rawContent = object?.data?.content || ''
  let displayContent = rawContent.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
  if (displayContent.length > 60) {
    displayContent = displayContent.slice(0, 60) + '…'
  }
  const typeLabel = objectType || 'node'
  const titleContent = displayContent || '(empty)'
  const modalTitle = `Set Kind for "${titleContent}" ${typeLabel}`

  return (
    <div className={cn('footer-meta')}>
      {isOpen ? (
        <PermissionOptionList {...(creator === undefined ? {} : { creator })} readers={readers} writers={writers} viewOnly={true} />
      ) : null}
      {isOpen && isEditing ? (
        <>
          <div
            className={cn('kind-indicator', { clickable: isOpen && isEditing, placeholder: kindColor == null })}
            role="button"
            tabIndex={0}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            onTouchStart={(e: React.TouchEvent<HTMLDivElement>) => e.stopPropagation()}
            onClick={openKindModal}
            onTouchEnd={openKindModal}
            onKeyDown={onKindIndicatorKeyDown}
          >
            <span className={cn('kind-dot')} style={{ backgroundColor: kindColor }} />
            <span
              className={cn('kind-label', { placeholder: kindColor == null })}
              style={kindColor != null ? { color: kindColor } : undefined}
            >
              {kindLabelToShow}
            </span>
          </div>
          <KindSelectModal
            open={kindModalOpen}
            onClose={closeKindModal}
            kinds={kinds}
            kindId={kindId}
            title={modalTitle}
            onSelectKind={onSelectKind}
            editOneKind={editOneKind}
            createOneKind={createOneKind}
            objectType={objectType}
          />
        </>
      ) : null}
      <div className={cn('modified-date')}>{modifiedString}</div>
    </div>
  )
}

export default FooterMeta
